import { memo, useState, useRef, useCallback, useMemo, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { cn } from "@/shared/utils/general/cn"
import { ObjectType } from "@/db/schema"
import { useStaticDataImmediate } from "@/features/static-data/useStaticData"
import { DynamicIcon } from "@/features/navigation/DynamicIcon"
import { StaticIcon } from "@/shared/icons/StaticIcon"
import { Dialog, DialogContent, DialogTitle } from "@/app/components/ui/dialog"
import type { MenuItem } from "@/features/static-data/useStaticData"
import type { RegisteredIconType } from "@/shared/icons/iconRegistry"
import {
  PickerList,
  PickerSearch,
  PickerItem,
  PickerSeparator,
  PickerEmpty,
} from "@/shared/components/picker"

// ---------------------------------------------------------------------------
// Row types for the structured picker list
// ---------------------------------------------------------------------------

interface VirtualItem {
  kind: "virtual"
  id: string
  title: string
  iconType: RegisteredIconType
}

interface ContainerItem {
  kind: "container"
  item: MenuItem
}

interface HeaderRow {
  kind: "header"
  label: string
}

interface SeparatorRow {
  kind: "separator"
}

type PickerRow = VirtualItem | ContainerItem | HeaderRow | SeparatorRow

interface ContainerPickerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Called when user selects a container */
  onSelect: (containerId: string, containerTitle: string) => void
  /** Current container ID to mark as active */
  currentContainerId?: string
  /** Show virtual containers (Inbox, Anyday) at top (default: false) */
  showVirtualContainers?: boolean
}

/**
 * Modal dialog for picking a container (project, list, area, or virtual).
 *
 * Features:
 * - Type-ahead autocomplete with keyboard navigation
 * - Virtual containers (Inbox, Anyday) at top when enabled
 * - Area group headers as visual separators
 * - Later (deferred) projects shown dimmed with "Later" badge
 * - Colored StaticIcon / TimelineIcon for every row
 * - Checkmark on current selection
 */
export const ContainerPicker = memo(function ContainerPicker({
  open,
  onOpenChange,
  onSelect,
  currentContainerId,
  showVirtualContainers = false,
}: ContainerPickerProps) {
  const { t } = useTranslation()
  const [query, setQuery] = useState("")
  const [highlightIndex, setHighlightIndex] = useState(-1)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const [staticData] = useStaticDataImmediate()

  // ── Build structured rows ──────────────────────────────────────────
  const { rows, selectableIndices } = useMemo(() => {
    const result: PickerRow[] = []
    const selectable: number[] = []
    const q = query.toLowerCase().trim()

    // 1. Virtual containers
    if (showVirtualContainers) {
      const virtuals: VirtualItem[] = [
        { kind: "virtual", id: "#inbox", title: t("Inbox"), iconType: "inbox" },
        { kind: "virtual", id: "#anyday", title: t("Anyday"), iconType: "anyday" },
      ]
      for (const v of virtuals) {
        if (!q || v.title.toLowerCase().includes(q)) {
          selectable.push(result.length)
          result.push(v)
        }
      }
    }

    // 2. Real containers from allMenuItems (includes later items)
    const allItems = staticData?.allMenuItems ?? []

    // When searching, flatten — no headers. When browsing, show area headers.
    if (q) {
      // Add separator after virtuals if both exist
      if (result.length > 0) {
        result.push({ kind: "separator" })
      }

      // Prefix matches first, then contains
      const prefix: MenuItem[] = []
      const contains: MenuItem[] = []
      for (const item of allItems) {
        const title = item.title.toLowerCase()
        if (title.startsWith(q)) prefix.push(item)
        else if (title.includes(q)) contains.push(item)
      }
      for (const item of [...prefix, ...contains]) {
        selectable.push(result.length)
        result.push({ kind: "container", item })
      }
    } else {
      // Browse mode — grouped by area with headers
      let hasOrphans = false
      let insideArea = false

      for (const item of allItems) {
        if (item.type === ObjectType.Area) {
          // Separator before first area (if orphans preceded it)
          if (!insideArea && hasOrphans) {
            result.push({ kind: "separator" })
          }
          insideArea = true
          selectable.push(result.length)
          result.push({ kind: "container", item })
          continue
        }

        if (!insideArea) hasOrphans = true

        selectable.push(result.length)
        result.push({ kind: "container", item })
      }
    }

    return { rows: result, selectableIndices: selectable }
  }, [staticData?.allMenuItems, query, showVirtualContainers, t])

  // Map from selectable position → highlight position
  const selectableCount = selectableIndices.length

  // Auto-highlight first selectable when filter changes
  useEffect(() => {
    setHighlightIndex(selectableCount > 0 ? 0 : -1)
  }, [selectableCount])

  // Reset when dialog opens
  useEffect(() => {
    if (open) {
      setQuery("")
      setHighlightIndex(-1)
    }
  }, [open])

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightIndex < 0 || !listRef.current) return
    const rowIdx = selectableIndices[highlightIndex]
    if (rowIdx == null) return
    const el = listRef.current.querySelector(`[data-row="${rowIdx}"]`) as
      | HTMLElement
      | undefined
    el?.scrollIntoView({ block: "nearest" })
  }, [highlightIndex, selectableIndices])

  const handleSelectVirtual = useCallback(
    (v: VirtualItem) => {
      onSelect(v.id, v.title)
      onOpenChange(false)
    },
    [onSelect, onOpenChange],
  )

  const handleSelectContainer = useCallback(
    (item: MenuItem) => {
      onSelect(item.id, item.title)
      onOpenChange(false)
    },
    [onSelect, onOpenChange],
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault()
        setHighlightIndex((prev) =>
          prev < selectableCount - 1 ? prev + 1 : 0,
        )
      } else if (e.key === "ArrowUp") {
        e.preventDefault()
        setHighlightIndex((prev) =>
          prev > 0 ? prev - 1 : selectableCount - 1,
        )
      } else if (e.key === "Enter") {
        e.preventDefault()
        const rowIdx = selectableIndices[highlightIndex]
        if (rowIdx == null) return
        const row = rows[rowIdx]
        if (row?.kind === "virtual") handleSelectVirtual(row)
        else if (row?.kind === "container") handleSelectContainer(row.item)
      }
    },
    [
      selectableCount,
      selectableIndices,
      highlightIndex,
      rows,
      handleSelectVirtual,
      handleSelectContainer,
    ],
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-action-panel
        showCloseButton={false}
        className="max-w-sm gap-0 p-0 overflow-hidden"
        onOpenAutoFocus={(e) => {
          e.preventDefault()
          requestAnimationFrame(() => inputRef.current?.focus())
        }}
      >
        <DialogTitle className="sr-only">{t("Move to…")}</DialogTitle>

        {/* Search input */}
        <PickerSearch
          value={query}
          onChange={setQuery}
          onKeyDown={handleKeyDown}
          placeholder={t("Move to…")}
          inputRef={inputRef}
          showIcon={false}
        />

        {/* Structured list */}
        <PickerList ref={listRef} className="max-h-[60vh] py-1">
          {selectableCount === 0 && (
            <PickerEmpty>{t("No matching containers")}</PickerEmpty>
          )}

          {rows.map((row, rowIdx) => {
            if (row.kind === "separator") {
              return <PickerSeparator key={`sep-${rowIdx}`} className="my-1" />
            }

            if (row.kind === "header") {
              return (
                <div
                  key={`hdr-${rowIdx}`}
                  className="px-3 pb-0.5 pt-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/60"
                >
                  {row.label}
                </div>
              )
            }

            // Find highlight position for this selectable row
            const selectPos = selectableIndices.indexOf(rowIdx)
            const isHighlighted = selectPos === highlightIndex

            if (row.kind === "virtual") {
              const isCurrent = row.id === currentContainerId
              return (
                <PickerItem
                  key={row.id}
                  data-row={rowIdx}
                  aria-selected={isHighlighted}
                  highlighted={isHighlighted}
                  selected={isCurrent}
                  icon={<StaticIcon type={row.iconType} size={20} mode="color" active />}
                  onClick={() => handleSelectVirtual(row)}
                  onMouseEnter={() => setHighlightIndex(selectPos)}
                >
                  {row.title}
                </PickerItem>
              )
            }

            // Container item (project / list / area)
            const { item } = row
            const isCurrent = item.id === currentContainerId
            const percentage =
              item.totalCount > 0
                ? Math.round((item.completedCount / item.totalCount) * 100)
                : 0

            return (
              <PickerItem
                key={item.id}
                data-row={rowIdx}
                aria-selected={isHighlighted}
                highlighted={isHighlighted}
                selected={isCurrent}
                icon={<ContainerIcon item={item} percentage={percentage} />}
                trailing={
                  item.isLater ? (
                    <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                      {t("Later")}
                    </span>
                  ) : undefined
                }
                onClick={() => handleSelectContainer(item)}
                onMouseEnter={() => setHighlightIndex(selectPos)}
                className={cn(
                  item.isLater && "opacity-50",
                  item.type === ObjectType.Area && "mt-2 font-semibold",
                )}
              >
                {item.title}
              </PickerItem>
            )
          })}
        </PickerList>
      </DialogContent>
    </Dialog>
  )
})

// ---------------------------------------------------------------------------
// Container icon helper
// ---------------------------------------------------------------------------

function ContainerIcon({
  item,
  percentage,
}: {
  item: MenuItem
  percentage: number
}) {
  if (item.type === ObjectType.Project || item.type === ObjectType.List) {
    return (
      <DynamicIcon
        icon={item.icon}
        type={item.type === ObjectType.Project ? "project" : "list"}
        percentage={percentage}
        unfinished={item.unfinishedCount}
        status={item.status}
        size="small"
      />
    )
  }
  return <StaticIcon type="area" size={20} mode="color" />
}
